import React, { Fragment, useCallback, useEffect, useState } from 'react';
import { Card, Box, Heading, Text, Button } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { ICourse, IImportCourse, IImportOffer, IOffer } from '../../../types';
import { useDispatch } from 'react-redux';
import * as offerActions from '../../../store/offers/actions';
import { ProcessingMode } from '../../pages/intakeSelector';
import { CoreStatusExtended, IntakeStatus } from 'types';

interface IProps {
  items: Array<IOffer>;
  mode: ProcessingMode;
}
const getOfferUniqueKey = (offer: IOffer) => `${offer.intakeId}#${offer.offerId}`;

const renderCourseTitle = (course: ICourse | IImportCourse, index: number) => {
  const title = course.courseName || (course as IImportCourse).title;
  return title ? (
    <Fragment key={index}>
      {course.courseName || (course as IImportCourse).title}
      <br />
    </Fragment>
  ) : null;
};

const CardItems: React.FC<IProps> = ({ items, mode }: IProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [sortedItems, setSortedItems] = useState<Array<IOffer>>([]);
  const [selected, setSelected] = useState<string | null>(null);

  useEffect(() => {
    const selectedOffer = items.find(i => i.isActive);
    if (selectedOffer) setSelected(getOfferUniqueKey(selectedOffer));

    if (selectedOffer) {
      const sortedItems = items.filter(i => getOfferUniqueKey(i) !== getOfferUniqueKey(selectedOffer));
      setSortedItems([selectedOffer, ...sortedItems]);
    } else {
      setSortedItems(items);
    }
  }, []);

  const handleSelectOffer = (offer: IOffer | IImportOffer) => {
    dispatch(offerActions.setActiveOffer(offer));
    if (offer.intakeStatus !== IntakeStatus.FUTURE && offer.status !== CoreStatusExtended.NOT_TRIGGERED) {
      navigate('/');
      return;
    }
    navigate('/inactive-intake');
  };

  const getButtonAttributes = useCallback(
    ({
      intakeStatus,
      isActive,
      status
    }: IOffer): {
      children: React.ReactNode;
      variant?: string;
      colorScheme?: string;
      isDisabled?: boolean;
    } => {
      if (intakeStatus === IntakeStatus.FUTURE || status === CoreStatusExtended.NOT_TRIGGERED) {
        return { children: 'Too Early for CAS' };
      }
      if (isActive) {
        return { children: 'Continue', variant: 'solid' };
      }
      if (selected) {
        return { children: `Change to this ${mode}` };
      }
      return { children: `Select ${mode}`, variant: 'solid' };
    },
    [selected, mode]
  );

  return (
    <>
      {sortedItems.map(offer => {
        const { intakeLabel, isActive, courses } = offer;

        const buttonAttributes = getButtonAttributes(offer);

        return (
          <Card
            key={getOfferUniqueKey(offer)}
            data-selector="card-item"
            variant="filled"
            flexDirection={{ base: 'column', md: 'row' }}
            alignItems={{ base: 'initial', md: 'center' }}
            justifyContent={{ base: 'initial', md: 'space-between' }}
            gap={3}
            mb={6}
            px={{ base: 6, md: 8 }}
            py={6}
            borderRadius="xl"
          >
            <Box id={`${intakeLabel}-label`}>
              <Heading as="h2" fontSize="xl" mb={1} wordBreak="break-word">
                {intakeLabel}

                {isActive ? (
                  <>
                    {' '}
                    <Box as="span" color="muted">
                      (active)
                    </Box>
                  </>
                ) : null}
              </Heading>

              <Text color="muted" mb={0}>
                {courses && courses.map(renderCourseTitle)}
              </Text>
            </Box>

            <Button onClick={() => handleSelectOffer(offer)} {...buttonAttributes} />
          </Card>
        );
      })}
    </>
  );
};

export default CardItems;
